import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { DataSet } from 'vis-data';
import { Timeline } from 'vis-timeline/standalone';

import { Colors } from '../../styles/themes';

import type { Medication } from './MedicationsDataBlock';

import 'vis-timeline/styles/vis-timeline-graph2d.css';

interface MedicationTimelineProps {
  medications: Medication[];
}

type TimelineMedication = Medication & { name?: string | null };

const trimValue = (value?: string | null) => {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getMedicationName = (med: TimelineMedication) =>
  trimValue(med.medication_name) ?? trimValue(med.name) ?? 'Unnamed medication';

const formatGenericName = (value?: string | null) => trimValue(value) ?? 'Unknown generic';

const formatDose = (med: TimelineMedication) => {
  const dosage = trimValue(med.dosage_amount);
  const strength = trimValue(med.strength);

  if (dosage && strength) {
    return `${dosage} (${strength})`;
  }

  return dosage ?? strength ?? 'Dose not specified';
};

const formatFrequency = (value?: string | null, { withFallbackLabel = true } = {}) => {
  const frequency = trimValue(value);
  if (frequency) return frequency;
  return withFallbackLabel ? 'Frequency not specified' : undefined;
};

const formatRoute = (value?: string | null) => trimValue(value) ?? 'Route not specified';

const formatIndication = (value?: string | null, { allowEmpty = false } = {}) => {
  const indication = trimValue(value);
  if (indication) return indication;
  return allowEmpty ? undefined : 'Indication not documented';
};

const formatDateLabel = (value?: string | null, { defaultLabel = 'Not specified' } = {}) => {
  if (!value) return defaultLabel;
  return moment(value).isValid() ? moment(value).format('MMM D, YYYY') : defaultLabel;
};

const formatTimelineContent = (med: TimelineMedication) => {
  const dose = formatDose(med);
  const frequency = formatFrequency(med.frequency, { withFallbackLabel: false });
  return frequency ? `${dose} • ${frequency}` : dose;
};

// Simple visual sub-timeline component for showing historical variations
const MedicationSubTimeline: React.FC<{ medications: TimelineMedication[] }> = ({
  medications,
}) => {
  // Sort medications by start date
  const sortedMedications = [...medications].sort(
    (a, b) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf(),
  );

  // Calculate the total date range for positioning
  const allDates = medications.flatMap(m => [
    moment(m.start_date).valueOf(),
    m.end_date ? moment(m.end_date).valueOf() : moment().valueOf(),
  ]);
  const minDate = Math.min(...allDates);
  const maxDate = Math.max(...allDates);
  const totalRange = maxDate - minDate;

  if (medications.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}
      >
        Historical Variations
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: '600px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sortedMedications.map((med, index) => {
            const start = moment(med.start_date).valueOf();
            const end = med.end_date ? moment(med.end_date).valueOf() : moment().valueOf();
            const leftPercent = ((start - minDate) / totalRange) * 100;
            const widthPercent = ((end - start) / totalRange) * 100;
            const isActive = med.isActive;
            const name = getMedicationName(med);

            return (
              <Box
                key={`${name}-${med.start_date ?? 'no-date'}-${index}`}
                sx={{ position: 'relative' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box
                    sx={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      bgcolor: isActive ? Colors.jotpsych_main : Colors.grey400,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: '70px' }}
                  >
                    {formatDose(med)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {formatFrequency(med.frequency)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                  >
                    {formatDateLabel(med.start_date, { defaultLabel: 'Unknown' })} -{' '}
                    {med.end_date
                      ? formatDateLabel(med.end_date, { defaultLabel: 'Unknown' })
                      : 'Ongoing'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    height: '6px',
                    bgcolor: 'background.paper',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`,
                      height: '100%',
                      bgcolor: isActive ? Colors.jotpsych_main : Colors.grey400,
                      borderRadius: '3px',
                      opacity: isActive ? 1 : 0.7,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

const MedicationTimeline: React.FC<MedicationTimelineProps> = ({ medications }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const [selectedItem, setSelectedItem] = useState<TimelineMedication | null>(null);

  // Filter medications for sub-timeline based on selected item's generic name
  const historicalVariations = useMemo(() => {
    if (!selectedItem || !selectedItem.generic_name) return [];
    return medications.filter(m => m.generic_name === selectedItem.generic_name);
  }, [selectedItem, medications]);

  useEffect(() => {
    if (!containerRef.current) return;

    // 1. Create Groups (Tracks) based on Generic Name
    // We want unique tracks for each generic medication
    const uniqueGenerics = Array.from(
      new Set(medications.map(m => m.generic_name).filter((name): name is string => name !== null)),
    );
    const groups = new DataSet<{ id: string; content: string; order: number }>(
      uniqueGenerics.map((generic, index) => ({
        id: generic,
        content: generic,
        order: index, // Keep original order or sort? Original might be chronological if data is sorted.
      })),
    );

    // 2. Create Items
    const items = new DataSet<{
      id: string;
      group: string;
      start: Date;
      end: Date;
      content: string;
      title: string;
      className: string;
      data: Medication;
      type: string;
    }>(
      medications
        .filter(
          (med): med is TimelineMedication & { generic_name: string } => med.generic_name !== null,
        ) // Only include medications with generic names
        .map((med, index) => {
          const start = moment(med.start_date).toDate();
          const end = med.end_date ? moment(med.end_date).toDate() : new Date();
          const name = getMedicationName(med);
          const indication = formatIndication(med.indication, { allowEmpty: true });

          // Determine style class based on status
          const className = med.isActive ? 'active-medication' : 'inactive-medication';

          return {
            id: `${name}-${med.start_date ?? 'no-start'}-${index}`, // Unique ID
            group: med.generic_name,
            start: start,
            end: end,
            content: formatTimelineContent(med),
            title: indication ? `${name} – ${indication}` : name,
            className: className,
            data: med, // Store full object for click handling
            type: 'range',
          };
        }),
    );

    // 3. Configuration
    const options = {
      height: '400px',
      start: moment().subtract(5, 'years').toDate(), // Default view window
      end: moment().add(6, 'months').toDate(),
      editable: false,
      stack: false, // Don't stack items if they overlap, let them overlap or use sub-lanes?
      // Actually, if we group by generic, dose changes might overlap in time (titration).
      // 'stack: true' prevents visual collision.
      stackSubgroups: true,
      zoomMin: 1000 * 60 * 60 * 24 * 7, // Min zoom: 1 week
      zoomMax: 1000 * 60 * 60 * 24 * 365 * 10, // Max zoom: 10 years
      orientation: { axis: 'top', item: 'top' },
      verticalScroll: true,
      horizontalScroll: true, // Enable horizontal scrolling with mouse wheel/trackpad
      zoomKey: 'ctrlKey', // Zoom with ctrl + scroll to avoid accidental zooming while scrolling page
    };

    // 4. Initialize Timeline
    // @ts-expect-error - vis-timeline types can be tricky with DataSet generics
    timelineRef.current = new Timeline(containerRef.current, items, groups, options);

    // Add a custom time bar for "now"
    timelineRef.current.addCustomTime(new Date(), 'now');
    timelineRef.current.setCustomTimeTitle('Current Date', 'now');

    // 5. Event Listeners
    timelineRef.current.on('select', (properties: { items: number[] }) => {
      if (properties.items && properties.items.length > 0) {
        const itemId = properties.items[0];
        // items.get(id) returns a single item if a single ID is passed
        const item = items.get(itemId) as unknown as { data: Medication };
        if (item && item.data) {
          setSelectedItem(item.data);
        }
      } else {
        setSelectedItem(null);
      }
    });

    return () => {
      if (timelineRef.current) {
        try {
          timelineRef.current.destroy();
        } catch (error) {
          // Timeline may already be destroyed, ignore errors
        }
        timelineRef.current = null;
      }
    };
  }, [medications]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'row',
        gap: 0,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: selectedItem ? 1 : 1,
          minWidth: selectedItem ? '67%' : '100%',
          transition: 'min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            '& .vis-timeline': { border: 'none', fontFamily: 'inherit' },
            '& .vis-item': {
              borderRadius: '6px',
              fontSize: '0.75rem',
              borderWidth: '0px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'transform 0.1s, box-shadow 0.1s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                zIndex: 100,
              },
            },
            '& .vis-item.vis-range': { display: 'flex', alignItems: 'center', paddingLeft: '4px' },
            '& .active-medication': {
              backgroundColor: Colors.jotpsych_main,
              color: 'white',
              background: `linear-gradient(135deg, ${Colors.jotpsych_main} 0%, ${Colors.primary500} 100%)`,
            },
            '& .inactive-medication': {
              backgroundColor: Colors.grey400,
              color: 'white',
              background: `linear-gradient(135deg, ${Colors.grey400} 0%, ${Colors.grey600} 100%)`,
              opacity: 0.8,
            },
            '& .vis-label': { color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' },
            '& .vis-group-level': { backgroundColor: 'transparent' },
            '& .vis-custom-time.now': { backgroundColor: Colors.danger, width: '2px' },
            '& .vis-time-axis .vis-text': { color: 'text.secondary' },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          * Scroll to move, Ctrl+Scroll to zoom. Click an item for details.
        </Typography>
      </Box>

      <AnimatePresence>
        {selectedItem && (
          <motion.div
            key={`${getMedicationName(selectedItem)}-${selectedItem.start_date || 'no-date'}`}
            initial={{ opacity: 0, width: 0, x: -20 }}
            animate={{ opacity: 1, width: '33%', x: 0 }}
            exit={{ opacity: 0, width: 0, x: -20 }}
            transition={{
              duration: 0.35,
              ease: [0.4, 0, 0.2, 1],
            }}
            style={{
              flexShrink: 0,
              minWidth: '300px',
              maxWidth: '33%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                pl: 3,
                ml: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
                height: '100%',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: Colors.jotpsych_main,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    flex: 1,
                  }}
                >
                  {getMedicationName(selectedItem)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setSelectedItem(null)}
                  sx={{
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Generic:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatGenericName(selectedItem.generic_name)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Dose:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatDose(selectedItem)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Frequency:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatFrequency(selectedItem.frequency)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Route:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatRoute(selectedItem.route_of_administration)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Start:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatDateLabel(selectedItem.start_date)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>End:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {selectedItem.end_date ? formatDateLabel(selectedItem.end_date) : 'Ongoing'}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <strong>Indication:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.8rem', textAlign: 'right', maxWidth: '60%' }}
                  >
                    {formatIndication(selectedItem.indication)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Chip
                    label={selectedItem.isActive ? 'Active' : 'Discontinued'}
                    sx={{
                      bgcolor: selectedItem.isActive ? Colors.jotpsych_main : Colors.grey300,
                      color: selectedItem.isActive ? 'white' : Colors.grey700,
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: '24px',
                    }}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Sub-timeline showing all historical variations */}
              {historicalVariations.length > 1 && (
                <MedicationSubTimeline medications={historicalVariations} />
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Paper>
  );
};

export default MedicationTimeline;
